import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../redux";
import { makeRequest } from "../utils/request";
import { Text, Spinner, Button, Card, CardContent, Icon } from "../components/base";

type Status = "loading" | "ready" | "submitting" | "success" | "denied" | "error";

// True when this page is rendered inside an iframe. The approval UI must never
// render while embedded — otherwise the button could be clickjacked. This runs
// in the same JS context that would draw the button, so an attacker can't grant
// scripts (needed to render the app) while suppressing this check.
const isFramed = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws → we are definitely framed.
    return true;
  }
};

export const CliLoginPage = () => {
  const [searchParams] = useSearchParams();
  // Read the user straight from the session slice, NOT via useAuth() — useAuth
  // mirrors session.user into local state through an effect, so it lags by a
  // render. That lag briefly shows user=null at the moment the session finishes
  // loading, which would bounce a signed-in user to /signin (and SignInPage,
  // reading session.user directly, bounces them right back → redirect loop).
  const user = useAppSelector((state) => state.session.user);
  const isLoadingSession = useAppSelector((state) => state.session.isLoadingSession);
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(5);
  const navigate = useNavigate();

  const token = searchParams.get("token");
  const framed = isFramed();

  // Redirect an unauthenticated visitor to sign in, preserving this URL so they
  // land back on the approval screen afterwards. Wait for the session fetch to
  // settle first: `user` is null both while loading AND when signed out, so
  // redirecting on null alone would bounce a just-signed-in user off this page
  // before their session hydrates. Nothing is approved on load — approval is an
  // explicit user action (see handleApprove).
  useEffect(() => {
    if (framed || !token || isLoadingSession) return;
    if (!user) {
      navigate(`/signin?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    }
  }, [user, navigate, framed, token, isLoadingSession]);

  // Once signed in, fetch the approval context: the account name to display and
  // a CSRF token the approval POST must echo. Nothing is granted by this call.
  useEffect(() => {
    if (framed || !token || !user) return;
    let cancelled = false;
    makeRequest<{ email: string; csrfToken: string }>("/v1/auth/cli-login/context", {
      method: "POST",
      body: { token },
    })
      .then((res) => {
        if (cancelled) return;
        setEmail(res.email);
        setCsrfToken(res.csrfToken);
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [user, token, framed]);

  // Strip the token from the URL as soon as the request is known to be bad, so
  // the invalid token isn't left in the address bar or browser history.
  useEffect(() => {
    if (status === "error" && token) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [status, token]);

  // Countdown + redirect after a failed approval. Redirect to a clean /signin
  // (no returnUrl) — the token is invalid/expired, so sending the user back to
  // this page with it would only fail again; they must re-run `vr signin`.
  useEffect(() => {
    if (status !== "error") return;
    if (countdown <= 0) {
      navigate("/signin", { replace: true });
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [status, countdown, navigate]);

  const handleApprove = () => {
    if (!token || !csrfToken) return;
    setStatus("submitting");
    makeRequest("/v1/auth/cli-complete-login", {
      method: "POST",
      body: { token, csrfToken },
    })
      .then(() => setStatus("success"))
      .catch((err) => {
        setError(err.message);
        setStatus("error");
      });
  };

  const handleDeny = () => setStatus("denied");

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">{children}</CardContent>
      </Card>
    </div>
  );

  if (framed) {
    return shell(
      <>
        <Icon
          name="shield"
          size={32}
          className="text-amber-500 mx-auto mb-3"
        />
        <Text
          variant="warning"
          className="font-semibold text-xl mb-2 block"
        >
          This page can't be embedded
        </Text>
        <Text
          variant="muted"
          className="block"
        >
          For your security, open the CLI sign-in link directly in your browser, not inside another site.
        </Text>
      </>,
    );
  }

  if (!token) {
    return shell(
      <>
        <Text
          variant="warning"
          className="font-semibold text-xl mb-2 block"
        >
          Missing login token
        </Text>
        <Text
          variant="muted"
          className="block"
        >
          This link is incomplete. Re-run the sign-in command in your terminal to get a fresh link.
        </Text>
      </>,
    );
  }

  if (status === "success") {
    return shell(
      <>
        <Icon
          name="check"
          size={32}
          className="text-green-500 mx-auto mb-3"
        />
        <Text
          variant="success"
          className="font-semibold text-xl mb-2 block"
        >
          CLI approved
        </Text>
        <Text
          variant="muted"
          className="block"
        >
          You can now return to your terminal. This window can be closed.
        </Text>
      </>,
    );
  }

  if (status === "denied") {
    return shell(
      <>
        <Text
          variant="warning"
          className="font-semibold text-xl mb-2 block"
        >
          Request denied
        </Text>
        <Text
          variant="muted"
          className="block"
        >
          No access was granted. If you didn't start this, you can safely close this window.
        </Text>
      </>,
    );
  }

  if (status === "error") {
    return shell(
      <>
        <Text
          variant="warning"
          className="font-semibold text-xl mb-2 block"
        >
          Couldn't complete sign-in
        </Text>
        <Text
          variant="muted"
          className="block mb-2"
        >
          {error || "The login request is invalid or has expired."}
        </Text>
        <Text
          variant="muted"
          className="block text-sm"
        >
          Redirecting to sign in in {countdown}s...
        </Text>
      </>,
    );
  }

  // Still hydrating the session, or fetching the approval context.
  if (status === "loading" || !user || !csrfToken) {
    return shell(
      <>
        <Spinner className="text-brand dark:text-brand-light w-8 h-8 mx-auto mb-4" />
        <Text
          variant="muted"
          className="block"
        >
          Checking your session...
        </Text>
      </>,
    );
  }

  // Signed in with a valid context: explicit approval screen. Nothing is sent
  // until Approve is clicked, and the approval carries the server-issued CSRF token.
  return shell(
    <>
      <Icon
        name="shield"
        size={32}
        className="text-brand dark:text-brand-light mx-auto mb-3"
      />
      <Text className="font-semibold text-xl mb-2 block">Authorize CLI sign-in</Text>
      <Text
        variant="muted"
        className="block mb-1"
      >
        A command-line client is requesting to sign in as
      </Text>
      <Text className="font-medium mb-4 block">{email || user.email}</Text>
      <Text
        variant="muted"
        className="block text-sm mb-6"
      >
        Only approve this if you just started a sign-in from your own terminal. Approving grants the CLI access to your VoiceRun account.
      </Text>
      <div className="flex flex-col gap-2">
        <Button
          variant="success"
          onClick={handleApprove}
          isLoading={status === "submitting"}
          loadingText="Approving..."
          disabled={status === "submitting"}
          className="justify-center w-full"
        >
          <Icon
            name="check"
            size={16}
          />
          Approve
        </Button>
        <Button
          variant="neutral"
          size="sm"
          outline
          onClick={handleDeny}
          disabled={status === "submitting"}
          className="justify-center w-full"
        >
          Deny
        </Button>
      </div>
    </>,
  );
};
